import { useReactiveVar } from '@apollo/client'
import { Dialog, Flex } from '@radix-ui/themes'
import { privyEmbeddedAddressesVar } from 'features/auth-privy/store'
import { DataTestIDs } from 'shared/constants'
import { usePlayWallet } from 'shared/hooks/use-play-wallet'
import { DialogShared } from 'shared/ui'
import { useLinkWallet } from '../hooks/use-link-wallet'
import { useWalletBalances } from '../hooks/use-wallet-balances'
import { isWalletsDialogOpenVar } from '../store/dialogs'
import { LinkWalletSection } from './link-wallet-section'
import { PlayWalletNotice } from './play-wallet-notice'
import { PrivyExportSection } from './privy-export-section'
import { WalletListItem } from './wallet-list-item'

/**
 * Your wallets (DEV-323): the account's wallets with the one selected for
 * play, why the connected wallet cannot play (not linked / not selected),
 * linking another wallet by signature and, with Privy, the embedded-key
 * export. Mounted once in `App`; opened from the header, Settings → Wallets
 * and the game buttons.
 */
export const WalletsDialog: React.FC = () => {
  const { wallets, selectedAddress, connectedAddress, status, selectWallet } =
    usePlayWallet()
  const { linkConnectedWallet, linkProviderWallet, isLinking } = useLinkWallet()
  const balances = useWalletBalances()
  const embeddedAddresses = useReactiveVar(privyEmbeddedAddressesVar)
  // NOTE: a user who signed in with Phantom has no embedded wallet to export
  const embeddedAddress = wallets
    .map(wallet => wallet.address)
    .find(address => embeddedAddresses.includes(address))

  return (
    <DialogShared
      openStore={isWalletsDialogOpenVar}
      width='64rem'
    >
      <Flex
        direction='column'
        gap='5'
        data-testid={DataTestIDs.walletsDialog}
      >
        <Dialog.Title mb='0'>Your wallets</Dialog.Title>

        <PlayWalletNotice
          status={status}
          connectedAddress={connectedAddress}
          isLinking={isLinking}
          onLinkConnected={linkConnectedWallet}
          onSelectConnected={selectWallet}
        />

        <Flex
          direction='column'
          gap='3'
        >
          {wallets.map(wallet => (
            <WalletListItem
              key={wallet.id}
              address={wallet.address}
              isPrimary={wallet.isPrimary}
              isSelected={wallet.address === selectedAddress}
              isConnected={wallet.address === connectedAddress}
              balance={balances[wallet.address]}
              onSelect={selectWallet}
            />
          ))}
        </Flex>

        <LinkWalletSection
          isLinking={isLinking}
          onLink={linkProviderWallet}
        />

        {embeddedAddress ?
          <PrivyExportSection address={embeddedAddress} />
        : null}
      </Flex>
    </DialogShared>
  )
}
